import {memo} from 'react';

import {createSelectorCreator, defaultMemoize} from 'reselect';

import type {ComponentType, FunctionComponent} from 'react';
const hasOwnProperty = Object.prototype.hasOwnProperty;

/**
 * inlined Object.is polyfill to avoid requiring consumers ship their own
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
 */
export function is(x: any, y: any): boolean {
  // SameValue algorithm
  if (x === y) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    // Added the nonzero y check to make Flow happy, but it is redundant
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    // Step 6.a: NaN == NaN
    return x !== x && y !== y;
  }
}

/**
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 * Returns true when the values of all keys are strictly equal.
 * Source: https://github.com/facebook/fbjs/blob/0c03b9c3788eaf19ec19598a00f319a5f0d831f2/packages/fbjs/src/core/shallowEqual.js
 */
export function shallowEqual(objA: any, objB: any): boolean {
  if (is(objA, objB)) {
    return true;
  }

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  for (let i = 0; i < keysA.length; i++) {
    if (
      !hasOwnProperty.call(objB, keysA[i]) ||
      !is(objA[keysA[i]], objB[keysA[i]])
    ) {
      return false;
    }
  }

  return true;
}
export function onlyUpdateForKeys<P>(
  Component: FunctionComponent<P>,
  keys: Array<string>
): ComponentType<P> {
  return memo<ComponentType<P>>(
    Component,
    (props, nextProps) =>
      !keys.some((k) => !shallowEqual(props[k], nextProps[k]))
  );
}

/*
 * @returns {bool} true if different, false if equal
 */
export const deepDiffer = function (one: unknown, two: unknown): boolean {
  if (one === two) {
    // Short circuit on identical object references instead of traversing them.
    return false;
  }

  if (typeof one === 'function' && typeof two === 'function') {
    // We consider all functions equal
    return false;
  }

  if (typeof one !== 'object' || one === null) {
    // Primitives can be directly compared
    return one !== two;
  }

  if (typeof two !== 'object' || two === null) {
    // We know they are different because the previous case would have triggered
    // otherwise.
    return true;
  }

  if (one.constructor !== two.constructor) {
    return true;
  }

  if (Array.isArray(one)) {
    // We know two is also an array because the constructors are equal
    const len = one.length;

    if ((two as Array<any>).length !== len) {
      return true;
    }

    for (let ii = 0; ii < len; ii++) {
      if (deepDiffer(one[ii], two[ii])) {
        return true;
      }
    }
  } else {
    for (let key in one) {
      if (deepDiffer(one[key], two[key])) {
        return true;
      }
    }

    for (let twoKey in two) {
      // The only case we haven't checked yet is keys that are in two but aren't
      // in one, which means they are different.
      if (one[twoKey] === undefined && two[twoKey] !== undefined) {
        return true;
      }
    }
  }

  return false;
};

/*
  selector for to be used with immutable objects as inputSelectors
  it uses Immutable.is as equalsFn
  https://medium.com/@tylorshin/using-reselect-with-immutablejs-772793913a9d
 */
export const createImmutableSelector = createSelectorCreator(
  defaultMemoize,
  is
);
